// Sessie en lidmaatschap. De server bepaalt rechten; de UI gebruikt ze alleen om te tonen wat kan.
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Permission } from "@bres/domain";
import { api } from "./api.ts";

export interface MemberInfo {
  userId: string;
  userName: string;
  userEmail: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  timezone: string;
  role: string;
  reviewLocales: string[];
  uiLocale: string;
  isSupport: boolean;
  permissions: Permission[];
}

export interface Me {
  authenticated: boolean;
  user?: { id: string; name: string; email: string };
  tenants?: { id: string; slug: string; name: string; is_demo: boolean; role: string; is_support: boolean; expires_at: string | null }[];
  member?: MemberInfo | null;
  /** Platformbeheerder (ADR 0007). Bepaalt alleen wat de UI toont; de server en database controleren zelf. */
  platform?: { admin: boolean };
}

const SessionContext = createContext<{ me: Me; member: MemberInfo | null } | null>(null);

export function useMeQuery() {
  return useQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/v1/me"), staleTime: 60_000 });
}

export function SessionProvider({ me, member, children }: { me: Me; member: MemberInfo | null; children: ReactNode }) {
  const qc = useQueryClient();
  useEffect(() => {
    const onLost = () => void qc.invalidateQueries({ queryKey: ["me"] });
    window.addEventListener("bres:unauthenticated", onLost);
    return () => window.removeEventListener("bres:unauthenticated", onLost);
  }, [qc]);
  return <SessionContext.Provider value={{ me, member }}>{children}</SessionContext.Provider>;
}

/** Sessie met een lidmaatschap dat ontbreken kan (platformbeheerder zonder klant). Voor de shell en het platform. */
export function useSessionState() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("SessionProvider ontbreekt");
  return value;
}

/** Sessie binnen een klant: schermen van een tenant renderen alleen met een actief lidmaatschap. */
export function useSession(): { me: Me; member: MemberInfo } {
  const value = useSessionState();
  if (!value.member) throw new Error("Geen actieve organisatie");
  return value as { me: Me; member: MemberInfo };
}

export function useIsPlatformAdmin(): boolean {
  return Boolean(useSessionState().me.platform?.admin);
}

export function useCan(): (permission: Permission) => boolean {
  const { member } = useSession();
  return (permission) => member.permissions.includes(permission);
}
